import { IPhoneNumber } from '@ViewModels';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useState } from 'react';
import { ContactViewModel } from '../../../extViewmodels';
import { getPartialFormattedPhoneNumber } from '../../../models/UiUtils';
import { useQueueLeadNote } from '../../contexts/queueLeadNote';
import { useTelephony } from '../../contexts/telephony';
import { useTysonMode } from '../../contexts/tysonMode';
import { useQueue } from '../../hooks/queue';
import { VoiceAgentModal } from '../../voice-agent/components/VoiceAgentModal';
import { NoneFound } from '../NoneFound';
import { CallButton, DirectLineCallButton } from '../TwilioDialer/CallButton';
import { styleSheet } from './styles';

interface IProps {
	className?: string;
	phoneNumbers: IPhoneNumber[];
}

const CallActionsBase: React.FC<IProps> = ({ className = '', phoneNumbers = [] }) => {
	const queue = useQueue();
	const { setShowQueueLeadNote } = useQueueLeadNote();
	const tysonMode = useTysonMode();
	const [isVoiceAgentModalOpen, setIsVoiceAgentModalOpen] = useState(false);
	const [selectedContactId, setSelectedContactId] = useState<string | null>(null);

	const { isPendingOutcome } = useTelephony();

	const onAddOutcome = () => {
		setShowQueueLeadNote(true);
	};

	// Find the selected contact if contactId is provided
	const selectedContact = selectedContactId
		? queue.lead?.contacts?.find((c: ContactViewModel) => c.id === selectedContactId)
		: null;

	if (!phoneNumbers?.length) {
		return <NoneFound text='No numbers found.' />;
	}

	return (
		<div className={`${css(styleSheet.callActionsContainer)} ${className}`}>
			{phoneNumbers.map((p, index) => {
				if (!p?.metadata) {
					return null;
				}

				const isDirectLine = p.label?.toLowerCase() === 'direct';

				return (
					<div key={`${p.metadata?.e164}-${index}`} className={css(styleSheet.callActionContainer)}>
						<div className={css(styleSheet.left)}>
							<div className={css(styleSheet.triggerContainer)}>
								<span className={css(styleSheet.label)}>{p.label}</span>

								<span>{getPartialFormattedPhoneNumber(p.metadata?.standard)}</span>
							</div>
						</div>
						<div className={css(styleSheet.right)}>
							{isPendingOutcome ? (
								<button
									className={css(styleSheet.callCta, styleSheet.disabled)}
									onClick={onAddOutcome}
									key='logLastCallOutcome'
								>
									<span>Log last call outcome</span>
								</button>
							) : tysonMode.isActive ? // In Tyson mode, don't show the top-level Spar button (non-contact specific)
							// Only show Spar buttons in the contact cards
							null : isDirectLine ? (
								<DirectLineCallButton
									phoneNumber={p}
									companyId={queue.lead.company.id}
									leadServedId={queue.lead.leadServedId}
									key='directCallButton'
								/>
							) : (
								<CallButton
									phoneNumber={p}
									companyId={queue.lead.company.id}
									leadServedId={queue.lead.leadServedId}
									key='callButton'
								/>
							)}
						</div>
					</div>
				);
			})}
			{selectedContact && queue.lead?.company && (
				<VoiceAgentModal
					leadId={queue.lead.leadServedId}
					isOpen={isVoiceAgentModalOpen}
					onClose={() => {
						setIsVoiceAgentModalOpen(false);
						setSelectedContactId(null);
					}}
					contact={selectedContact.toJs()}
					company={queue.lead.company.toJs()}
				/>
			)}
		</div>
	);
};

export const CallActions = observer(CallActionsBase);
